/**
 * Card: the one surface for panels, sections and stat blocks.
 *
 *   <Card>...</Card>                                            white (dark: surface), 1px border, 12px radius, p-5
 *   <Card padding="none"><DataTable .../></Card>                 content runs edge to edge
 *   <Card variant="muted" padding="sm">Tip</Card>                  tinted, for callouts inside another card
 *   <Card variant="dashed">Create your first API key</Card>       empty placeholders
 *   <Card as="section" aria-labelledby="usage-title">...</Card>
 *
 *   <Card>
 *     <CardHeader title="Recent activity" description="Last 7 days" actions={<Button size="sm">View all</Button>} />
 *     ...
 *   </Card>
 *
 * padding none | sm (16px) | md (20px, default) | lg (24px). Do not put a bordered Card inside another Card; use
 * variant="muted" for the inner block.
 */
import { ElementType, HTMLAttributes, ReactNode } from "react";

type Padding = "none" | "sm" | "md" | "lg";
type Variant = "default" | "muted" | "dashed";

const PADDING: Record<Padding, string> = { none: "", sm: "p-4", md: "p-5", lg: "p-6" };
const VARIANT: Record<Variant, string> = {
  default: "bg-surface border border-line shadow-card",
  muted: "bg-surface-muted border border-line",
  dashed: "bg-transparent border border-dashed border-line-strong",
};

export interface CardProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
  padding?: Padding;
  variant?: Variant;
}

export default function Card({ as: Tag = "div", padding = "md", variant = "default", className = "", ...rest }: CardProps) {
  return <Tag className={`rounded-card ${VARIANT[variant]} ${PADDING[padding]} ${className}`} {...rest} />;
}

export function CardHeader({
  title,
  description,
  actions,
  as: Heading = "h2",
  id,
  className = "",
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  as?: "h2" | "h3";
  id?: string;
  className?: string;
}) {
  return (
    <div className={`mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-2 ${className}`}>
      <div className="min-w-0">
        <Heading id={id} className="text-base font-semibold text-fg">
          {title}
        </Heading>
        {description && <p className="mt-0.5 text-13 text-fg-muted">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
